/**
 * 🚀 리팩토링된 팀 성과 관리 페이지
 * 
 * 주요 개선사항:
 * - 컴포넌트 분리: PerformanceSummary, MonthlyStatistics, TeamPerformanceTable
 * - 커스텀 훅 분리: 날짜 네비게이션, 정렬, 새로고침 로직
 * - 유틸 함수 분리: 마스킹, 계산, 정렬 로직
 * - React.memo, useMemo, useCallback을 통한 성능 최적화
 * - IIFE 제거 및 변수 추출로 가독성 향상
 */
import React, { useState } from 'react';
import { useAppContext } from '../contexts/AppContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { format, addMonths, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useTeamPerformanceData } from '../hooks/useTeamPerformanceData.tsx';
import EmployeePerformanceTable from '../components/EmployeePerformanceTable.tsx';
import PerformanceSummary from '../components/PerformanceSummary.tsx';
import MonthlyStatistics from '../components/MonthlyStatistics.tsx';
import TeamPerformanceTable from '../components/TeamPerformanceTable.tsx';
import { sortTeamData } from '../utils/teamPerformanceUtils.ts';

const TeamPerformancePage: React.FC = () => {
  const { employees, budgetData, isLoading: contextLoading, teams, registerTeamPerformanceRefresh } = useAppContext();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const hasRegisteredRef = React.useRef<boolean>(false);
  const [teamSortConfig, setTeamSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // 🎯 직원 역할 사용자의 소속 팀 확인
  const userTeamName = React.useMemo(() => {
    if (user?.role === 'employee' && user.teamId) {
      const userTeam = teams.find(t => t.id === user.teamId);
      return userTeam?.name || null;
    }
    return null;
  }, [user, teams]);

  // 🎯 날짜 네비게이션 함수들
  const handlePreviousMonth = React.useCallback(() => {
    setCurrentDate(prev => subMonths(prev, 1));
  }, []);

  const handleNextMonth = React.useCallback(() => {
    setCurrentDate(prev => addMonths(prev, 1));
  }, []);
  
  // 🎯 팀 성과 데이터 가져오기
  const {
    performanceData,
    totalWorkingDays,  // 🆕 전체 근무일
    isLoading: dataLoading,
    isCalculating,
    lastCalculatedAt,
    dataSource,
    refreshData,
    error
  } = useTeamPerformanceData(currentDate);

  // 🆕 전체 일평균 매출 계산
  const totalDailyAverageSales = React.useMemo(() => {
    if (totalWorkingDays > 0) {
      const filteredData = user?.role === 'employee' && userTeamName
        ? performanceData.filter(team => team.teamName === userTeamName)
        : performanceData;

      const totalActualSales = filteredData.reduce((sum, team) => sum + team.actualSales, 0);
      return Math.round(totalActualSales / totalWorkingDays);
    }
    return 0;
  }, [performanceData, totalWorkingDays, user?.role, userTeamName]);


  // 🎯 정렬 핸들러
  const handleTeamSort = React.useCallback((key: string) => {
    let direction: 'asc' | 'desc' | null = 'desc';
    
    if (teamSortConfig && teamSortConfig.key === key) {
      if (teamSortConfig.direction === 'desc') {
        direction = 'asc';
      } else if (teamSortConfig.direction === 'asc') {
        direction = null;
      }
    }
    
    if (direction === null) {
      setTeamSortConfig(null);
    } else {
      setTeamSortConfig({ key, direction });
    }
  }, [teamSortConfig]);

  // 🎯 정렬된 팀 성과 데이터
  const sortedPerformanceData = React.useMemo(() => {
    if (!teamSortConfig) return performanceData;
    return sortTeamData(performanceData, teamSortConfig.key, teamSortConfig.direction);
  }, [performanceData, teamSortConfig]);

  // 🎯 새로고침 등록
  React.useEffect(() => {
    if (!hasRegisteredRef.current && registerTeamPerformanceRefresh && refreshData) {
      console.log('[TeamPerformancePage] 팀 성과 관리 새로고침 함수 등록 시도');
      registerTeamPerformanceRefresh(refreshData);
      hasRegisteredRef.current = true;
      console.log('[TeamPerformancePage] 팀 성과 관리 새로고침 함수 등록 완료');
    }
  }, [registerTeamPerformanceRefresh, refreshData]);

  // 🎯 성과 요약 데이터 계산
  const performanceSummary = React.useMemo(() => {
    const filteredData = user?.role === 'employee' && userTeamName
      ? (performanceData || []).filter(team => team.teamName === userTeamName)
      : (performanceData || []);

    const summary = filteredData.reduce((acc, team) => ({
      totalTargetSales: acc.totalTargetSales + team.targetSales,
      totalActualSales: acc.totalActualSales + team.actualSales,
      totalExceededSales: acc.totalExceededSales + team.exceededSales,
      totalUnderperformanceSales: acc.totalUnderperformanceSales + team.underperformanceSales,
      totalDeductionAmount: acc.totalDeductionAmount + team.deductionAmount,
      totalIncentiveAmount: acc.totalIncentiveAmount + team.incentiveAmount,
    }), {
      totalTargetSales: 0,
      totalActualSales: 0,
      totalExceededSales: 0,
      totalUnderperformanceSales: 0,
      totalDeductionAmount: 0,
      totalIncentiveAmount: 0,
    });

    const achievementRate = summary.totalTargetSales > 0 
      ? (summary.totalActualSales / summary.totalTargetSales) * 100
      : 0;

    return {
      ...summary,
      achievementRate
    };
  }, [performanceData, user?.role, userTeamName]);

  // 🎯 월별 직원 데이터 (월별 데이터 우선, 없으면 전역 데이터)
  const targetEmployees = React.useMemo(() => {
    const yearMonth = format(currentDate, 'yyyy-MM');
    const monthlyData = budgetData?.monthlyData?.[yearMonth];
    return monthlyData?.employees?.length > 0 ? monthlyData.employees : employees;
  }, [currentDate, budgetData, employees]);

  // 🎯 팀원 수 계산 함수
  const getTeamMemberCount = React.useCallback((teamName: string): number => {
    // 🔧 월급(세전) 0원 직원은 인센티브 배분 대상이 아니므로 팀원 수에서 제외
    return targetEmployees.filter(emp => emp.team === teamName && emp.salary > 0).length;
  }, [targetEmployees]);

  // 🎯 데이터 소스 정보 — 정본 안내(title)와 함께 표시. '데이터 없음'은 경고색(회색 금지).
  const dataSourceInfo = React.useMemo(() => {
    if (dataSource === 'cached') {
      return {
        icon: '💾',
        text: '저장된 계산 결과',
        color: 'text-blue-700',
        title: '마지막으로 저장된 계산 결과입니다. [데이터 새로고침]을 누르면 최신 일일보고 기준으로 재계산되어 이 값이 갱신됩니다.'
      };
    } else if (dataSource === 'calculated') {
      return {
        icon: '🔄',
        text: '실시간 계산',
        color: 'text-green-700',
        title: '방금 일일보고 원본에서 재계산된 최신 값입니다. 저장된 결과도 이 값으로 갱신되었습니다.'
      };
    } else {
      return {
        icon: '⚠️',
        text: '데이터 없음',
        color: 'text-orange-700',
        title: '이 달의 일일보고 데이터가 없습니다. 일일보고를 업로드하면 성과가 계산됩니다.'
      };
    }
  }, [dataSource]);

  // 로딩 상태
  if (contextLoading || dataLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {contextLoading ? '기본 데이터를 불러오는 중...' : '팀 성과 데이터를 불러오는 중...'}
          </p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                데이터 로드 오류
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={refreshData}
                  className="bg-red-100 px-4 py-2 rounded-lg text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                  disabled={isCalculating}
                >
                  {isCalculating ? '재계산 중...' : '다시 시도'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* 헤더 */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center space-x-3 mb-4 md:mb-0">
          <h1 className="text-2xl font-semibold text-gray-800">팀 성과 관리</h1>
          <div className="flex items-center space-x-2">
            <span className={`text-sm cursor-help ${dataSourceInfo.color}`} title={dataSourceInfo.title}>
              {dataSourceInfo.icon} {dataSourceInfo.text}
            </span>
            {lastCalculatedAt && (
              <span className="text-xs text-gray-500">
                ({format(new Date(lastCalculatedAt), 'HH:mm:ss')})
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* 새로고침 버튼 */}
          <button
            onClick={refreshData}
            disabled={isCalculating}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCalculating ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-t border-white"></div>
                <span>계산 중...</span>
              </div>
            ) : (
              '데이터 새로고침'
            )}
          </button>
          
          {/* 월 네비게이션 */}
          <button
            onClick={handlePreviousMonth}
            aria-label="이전 달"
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-lg font-medium text-gray-700">
            {format(currentDate, 'yyyy년 MM월', { locale: ko })}
          </span>
          <button
            onClick={handleNextMonth}
            aria-label="다음 달"
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* 🚀 성능 최적화: 전체 매출 요약을 분리된 컴포넌트로 교체 */}
      <PerformanceSummary
        totalTargetSales={performanceSummary.totalTargetSales}
        totalActualSales={performanceSummary.totalActualSales}
        achievementRate={performanceSummary.achievementRate}
        userRole={user?.role}
        userTeamName={userTeamName}
        totalWorkingDays={totalWorkingDays}
        totalDailyAverageSales={totalDailyAverageSales}
      />

      {/* 🎯 팀별 성과 테이블 - 분리된 컴포넌트 */}
      <TeamPerformanceTable
        data={sortedPerformanceData}
        onSort={handleTeamSort}
        sortConfig={teamSortConfig}
        userRole={user?.role}
        userTeamName={userTeamName}
        getTeamMemberCount={getTeamMemberCount}
      />

      {/* 직원별 성과 테이블 */}
      <EmployeePerformanceTable 
        employees={targetEmployees}
        teamPerformanceData={performanceData.reduce((acc, team) => ({
          ...acc,
          [team.teamName]: team
        }), {})}
        userRole={user?.role}
        userTeamName={userTeamName}
        userEmployeeId={user?.employeeId}
        budgetData={budgetData}
        useRealTimeIncentive={true}
        selectedMonth={format(currentDate, 'yyyy-MM')}
      />

      {/* 🎯 월간 통계 섹션 - 분리된 컴포넌트 */}
      <MonthlyStatistics
        totalExceededSales={performanceSummary.totalExceededSales}
        totalUnderperformanceSales={performanceSummary.totalUnderperformanceSales}
        totalDeductionAmount={performanceSummary.totalDeductionAmount}
        totalIncentiveAmount={performanceSummary.totalIncentiveAmount}
        userRole={user?.role}
        userTeamName={userTeamName}
      />
    </div>
  );
};

export default TeamPerformancePage; 